import { Output, Task } from './task.interface';
import { Document } from './document.interface';
import { ChatSession } from './chat.interface';
import { AiScribeJob } from './aiScribeJob.interface';
import { OrganizationUser } from './organization_user.interface';
import { ReportVariant } from './report.interface';

export type ReportType =
  | 'qme_report'
  | 'personal_injury_report'
  | 'medico_legal_evaluation'
  | 'treatment_provider'
  | 'testing_flow';

export interface Specialty {
  label: string;
  value: string; // snake_case code from API
  icon?: string;
}

export type QmeReportVariant = ReportVariant;

/**
 * Template picked from the QME Report-tab dropdown. Every kind is stored and
 * rendered as a `qme_report` output — the kind only selects which template the
 * consumer runs. 'qme' is the standard pipeline; every other kind is published
 * to the consumer as a `sibtf_template` value (see QME_REPORT_KIND_TEMPLATE in
 * the API's sessions.controller).
 *
 * To add a kind: add it here, add its label in QME_REPORT_KIND_LABELS, add an
 * <li> to the Report dropdown, and add its sibtf_template value in the API map.
 */
export type QmeReportKind =
  | 'qme'
  | 'sibtf'
  | 'med_legal_ptp'
  | 'ime_reeval_fishman'
  | 'pqme_reeval_fishman'
  | 'ame_reeval_fishman'
  | 'darakjian_initial_qme'
  | 'zuber_qme_history_exam'
  | 'johnson_qme';

export const QME_REPORT_KIND_LABELS: Record<QmeReportKind, string> = {
  qme: 'QME Report',
  sibtf: 'SIBTF Report',
  med_legal_ptp: 'Med Legal PTP Report',
  ime_reeval_fishman: 'Fishman IME Re-eval',
  pqme_reeval_fishman: 'Fishman PQME Re-eval',
  ame_reeval_fishman: 'Fishman AME Re-eval',
  darakjian_initial_qme: 'Darakjian Initial QME',
  zuber_qme_history_exam: 'Zuber QME History & Exam',
  johnson_qme: 'Johnson Psychiatric QME',
};

/**
 * `session_report_generation_statuses.status` is a code-enforced string (the DB
 * enum was dropped). Legacy report types use IDLE/RUNNING/COMPLETED/FAILED; the
 * Temporal PI flow uses the fine-grained values below to express live progress.
 * Treat any value other than IDLE/COMPLETED/FAILED/SKIPPED as in-progress.
 */
export type ReportGenerationStatusValue =
  | 'IDLE'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
  | 'SKIPPED'
  | 'QUEUED'
  | 'INPUT_CREATION_STARTED'
  | 'INPUT_CREATION_COMPLETE'
  | 'AI_GENERATION_QUEUED'
  | 'AI_GENERATION_STARTED'
  | 'AI_GENERATION_COMPLETED'
  | 'DOCUMENT_GENERATION_QUEUED'
  | 'DOCUMENT_GENERATION_STARTED'
  | 'DOCUMENT_GENERATION_COMPLETE'
  | 'RETRY_INPUT_CREATION'
  | 'RETRY_AI_GENERATION'
  | 'RETRY_DOCUMENT_GENERATION'
  | 'AI_EDITING';

type NullableDateLike = Date | string | null;

export interface SessionReportGenerationStatus {
  id: string;
  sessionId: string;
  outputType: string;
  status: ReportGenerationStatusValue;
  /** Sanitized failure code (FAILED/SKIPPED rows only). */
  errorCode?: string | null;
  /** Sanitized, truncated failure message (FAILED/SKIPPED rows only). */
  errorMessage?: string | null;
  requestedAt?: NullableDateLike;
  completedAt?: NullableDateLike;
  failedAt?: NullableDateLike;
  /**
   * Server-authoritative moment the most recent (re)generation STARTED. Unlike
   * {@link requestedAt} (cleared on completion), this survives after the run
   * finishes, so it is the reliable baseline for "were any inputs added since
   * this report's generation began?" — including AI Scribe edits made mid-run,
   * which land before the report's final `updatedAt`. Undefined for reports
   * generated before this field existed (callers fall back to a client-side
   * baseline / the report's updatedAt).
   */
  lastGenerationStartedAt?: NullableDateLike;
  createdAt: Date | string;
  updatedAt?: NullableDateLike;
}

/**
 * The "QME pipeline group" — report kinds that run as Temporal children of the
 * QME Report pipeline itself and are therefore mutually exclusive per session
 * (only applies when `Session.reportType === 'qme_report'`).
 */
export type QmePipelineGroupOutputType =
  | 'qme_report'
  | 'review_of_records'
  | 'impairment_rating';

/**
 * Answers the "force Impairment generation anyway?" prompt for a specific
 * strict-no MMI result. Sent as a pair — the backend only honors
 * `forceImpairment` when `forceDecisionForMmiOutputId` names the exact
 * `mmi_status` output currently cached as the strict-no result.
 */
export interface MmiForceDecisionParams {
  forceImpairment: boolean;
  forceDecisionForMmiOutputId: string;
}

/**
 * Authoritative, generation-free freshness read for the MMI gate and the
 * report kinds whose inputs are tracked server-side, used to reconcile a
 * client after reconnect or input drift. `reports[kind].isCurrent: false`
 * means that report kind's own tracked inputs no longer match current inputs
 * — its existing output (if any) is historical, not the current clinical
 * state.
 *
 * `reviewOfRecords` is carried here despite RoR not being MMI-gated: it is
 * resolved by the same report-kind-agnostic cache-hit dispatch, and riding
 * along on this one read avoids a second round trip per session.
 */
export interface MmiGateStateResponse {
  mmiRecheckRequired: boolean;
  mmiOutputId?: string;
  reports: {
    qmeReport?: { isCurrent: boolean; outputId?: string };
    impairmentRating?: { isCurrent: boolean; outputId?: string };
    reviewOfRecords?: { isCurrent: boolean; outputId?: string };
  };
  /**
   * A separate freshness concern from the MMI reports above: has Impairment
   * Rating or Review of Records been edited standalone since QME last
   * consumed it. Bundled into this same response to avoid a second round
   * trip for the same screen-load, not because it's an MMI-gate concept.
   */
  qmeInputStaleness?: { impairmentEdited: boolean; rorEdited: boolean };
}

/**
 * Client-derived, session-scoped state of an in-flight MMI gate check —
 * distinct from `MmiGateStateResponse`, which is the backend's freshness
 * read and has no notion of "currently checking". Persisted on the active
 * session (via the two `SessionStore.updateActiveSessionMmiGate*` methods)
 * rather than component-local signals so it survives a report component
 * being destroyed and recreated (e.g. switching tabs and back) while the
 * MMI gate's Temporal check is still running.
 *
 * Modeled as two independent fields, not one combined status, because a
 * stale/unrelated `mmi_status` event can legitimately flip "checking" back
 * to false while leaving an already-pending, still-valid force-decision
 * prompt (for a *different* MMI output) untouched — the two states are not
 * mutually exclusive at every instant, only in the request flows that set
 * either of them to an active value.
 */
export interface MmiGateCheckState {
  isChecking: boolean;
  /** The MMI output id a pending "force generation anyway?" prompt applies to, or null when none is pending. */
  pendingForceDecisionMmiOutputId: string | null;
}

/** Optional fields for one QME-generation request. */
export interface RegenerateQmeReportOptions {
  /** Sent only on the initial request, before the MMI gate can start. */
  selectionId?: string;
  /** Sent only when answering a strict-no MMI decision prompt. */
  mmiForceDecision?: MmiForceDecisionParams;
  /**
   * Dictation-only package: the ONE AI Scribe whose transcription the report
   * is built from. Sent on the initial request AND on a decision follow-up,
   * since the backend requires it whenever the saved package is dictation.
   */
  aiScribeId?: string;
}

/** Outcome of a `POST :sessionId/generate-impairment-rating` request. */
export type GenerateImpairmentRatingStatus =
  | 'CACHE_HIT'
  | 'QUEUED'
  | 'ALREADY_RUNNING'
  | 'BLOCKED_BY_RELATED_PIPELINE'
  | 'IMPAIRMENT_INPUTS_NOT_READY'
  /**
   * No current MMI result exists for this session. An MMI-only check was
   * queued; standalone Impairment itself has not started yet and only
   * begins once that check resolves (see the session's `mmi_status` output).
   */
  | 'MMI_CHECK_QUEUED'
  /**
   * The current MMI result is a strict "no" and there's no cached answer to
   * the force-decision question yet. `mmiOutputId` identifies the exact MMI
   * result being asked about — resubmit as a new request with
   * `forceImpairment` and `forceDecisionForMmiOutputId` set to this id.
   */
  | 'MMI_FORCE_DECISION_REQUIRED'
  /**
   * The caller answered "don't force" on a strict-no MMI result. Impairment
   * did not start; this is terminal, not an error.
   */
  | 'MMI_IMPAIRMENT_DECLINED';

export interface GenerateImpairmentRatingResponse {
  sessionId: string;
  success: boolean;
  code: number;
  status: GenerateImpairmentRatingStatus;
  /**
   * Absent when nothing was reserved: `IMPAIRMENT_INPUTS_NOT_READY`,
   * `MMI_FORCE_DECISION_REQUIRED`, and `MMI_IMPAIRMENT_DECLINED`.
   */
  workflowId?: string;
  /** Present only when `status === 'CACHE_HIT'`. */
  taskId?: string;
  /** Present only when `status === 'CACHE_HIT'`. */
  outputId?: string;
  /**
   * Present only when `status === 'MMI_FORCE_DECISION_REQUIRED'` or
   * `'MMI_IMPAIRMENT_DECLINED'` — the id of the `mmi_status` output the
   * force-decision question or decline applies to.
   */
  mmiOutputId?: string;
  /**
   * Present only when `status === 'BLOCKED_BY_RELATED_PIPELINE'`. Always
   * non-empty; more than one entry only when triggering `qme_report` while
   * both `review_of_records` and `impairment_rating` are active at once.
   */
  blockingOutputTypes?: QmePipelineGroupOutputType[];
  /**
   * Non-blocking exclusion notices (e.g. a failed document or AI scribe was
   * excluded from this generation). Never present alongside
   * `IMPAIRMENT_INPUTS_NOT_READY` — that status means generation didn't run.
   */
  warnings?: string[];
  message: string;
}

/**
 * Outcome of the QME Report / Review of Records trigger endpoints.
 * `'success'` (lowercase) is the legacy non-Temporal AMQP fallback path's
 * literal status value; the other members come from the Temporal path.
 */
export type ReportTriggerStatus =
  | 'QUEUED'
  | 'ALREADY_RUNNING'
  | 'BLOCKED_BY_RELATED_PIPELINE'
  | 'success'
  | 'QME_INPUTS_NOT_READY'
  | 'MMI_CHECK_QUEUED'
  | 'MMI_FORCE_DECISION_REQUIRED'
  // Summary queue: kept and will run after the current summary / upload.
  | 'PENDING'
  // Summary queue: a summary that finished after the click already covers
  // every document.
  | 'SKIPPED_NO_NEW_DOCUMENTS';

export interface ReportTriggerResponse {
  sessionId?: string;
  success: boolean;
  code?: number;
  status?: ReportTriggerStatus;
  workflowId?: string;
  /** Exact strict-no MMI output to reference in a force-decision follow-up. */
  mmiOutputId?: string;
  /**
   * Present only when `status === 'BLOCKED_BY_RELATED_PIPELINE'`. Always
   * non-empty; more than one entry only when triggering `qme_report` while
   * both `review_of_records` and `impairment_rating` are active at once.
   */
  blockingOutputTypes?: QmePipelineGroupOutputType[];
  /**
   * Non-blocking exclusion notices (e.g. a failed document or AI scribe was
   * excluded from this generation). Never present alongside
   * `QME_INPUTS_NOT_READY` — that status means generation didn't run.
   */
  warnings?: string[];
  message?: string;
}

export interface Session {
  id: string;
  value: string;
  specialty?: string;
  reportType?: ReportType;
  /** Persisted QME prompt variant (comprehensive / follow-up / supplemental). */
  qmeReportVariant?: QmeReportVariant | null;
  /** Persisted QME Report-dropdown template. Null / absent = never chosen (QME). */
  qmeReportKind?: QmeReportKind | null;
  claimantId?: string;
  createdBy?: string;
  createdAt: Date;
  claimNumber?: string;
  isRepresented?: boolean;
  dateOfInjury?: Date;
  updatedAt: Date;
  organization_id: string;
  panelIssuedAt?: Date;
  requiresInterpreter?: boolean;
  interpreterLanguage?: string;
  /** Injured worker's employer at the time of injury. */
  employerName?: string | null;
  /** Claims administrator company (carrier / TPA / self-insured employer). */
  claimsAdminName?: string | null;
  /** Adjuster / claims examiner. */
  claimsAdminContactName?: string | null;
  claimsAdminAddress?: string | null;
  claimsAdminCity?: string | null;
  claimsAdminState?: string | null;
  claimsAdminZipcode?: string | null;
  claimsAdminPhone?: string | null;
  claimsAdminFax?: string | null;
  claimsAdminEmail?: string | null;
  claimant?: Claimant;
  sessionMembers?: SessionMember[];
  task?: Task[];
  documents?: Document[];
  aiScribeJobs?: AiScribeJob[];
  chat_sessions?: ChatSession[];
  reportGenerationStatuses?: SessionReportGenerationStatus[];

  // Not a relation but for ease of use.
  activeAiScribeJob: AiScribeJob;
  summary?: Output;
  quickReference?: Output;
  impairmentRating?: Output;
  /** Latest backend-derived MMI status output (rabbitmq). */
  mmiStatus?: Output;
  qmeReport?: Output;
  medicalLegalReport?: Output;
  ptpReport?: Output;
  sibtfReport?: Output;
  piReport?: Output;
  physicalExaminationQuestions?: Output;
  reviewOfRecords?: Output;
  /**
   * Client-derived, not part of the raw backend Session response: the
   * authoritative MMI/report freshness read for reconnect and input-drift
   * reconciliation. Absent until the first successful gate-state fetch.
   */
  mmiGateState?: MmiGateStateResponse;
  /**
   * Client-derived, not part of the raw backend Session response: whether
   * an MMI gate check is currently in flight for this session, and — once
   * resolved to a strict-no result — the pending force-decision prompt.
   * Absent means no MMI gate check is currently in flight.
   */
  mmiGateCheckState?: MmiGateCheckState;
}

export interface SourceSession extends Omit<Session, 'sessionMembers'> {
  SessionMember: SessionMember[];
}

export interface SessionMember {
  id: string;
  userId: string;
  sessionId: string;
  name: string;
  email: string;
  role: SessionMemberRole;
  addedAt: string;
  active: boolean;
  addedBy?: string | null;
  notes?: string | null;
  persisted?: boolean; // Indicates if the member is saved in the backend
  user?: OrganizationUser;
}

export enum SessionMemberRole {
  EDITOR = 'EDITOR',
  VIEWER = 'VIEWER',
  ASSIGNEE = 'ASSIGNEE',
}

export interface SessionUserNote {
  id: string;
  sessionId: string;
  userId: string;
  content: string;
  createdAt: string;
  updatedAt: string | null;
}

export interface Claimant {
  id: string;
  organizationId: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string | null;
  phone?: string | null;
  email?: string | null;
  preferredLanguage?: string | null;
  requiresInterpreter?: boolean;
  interpreterLanguage?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface QmeProfile {
  id: string;
  organizationUserId: string;
  organizationId: string;
  licenseNumber: string | null;
  dwcPanelNumber: string | null;
  specialties: string[];
  maxEvalsPerDay: number | null;
  evalDurationMins: number | null;
  bufferMins: number | null;
  calendarProvider: string | null;
  calendarToken: string | null;
  calendarSyncedAt: string | null;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}
